import { Skeleton } from "@flexfreight/ui";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import type { ReactNode } from "react";
import { ConsoleMeContext } from "./context";
import { loadConsoleSession } from "./session";
import { MfaRequired, SignIn, StaffOnly, VerifyCode } from "./SignIn";

export const SESSION_KEY = ["console-session"] as const;

/**
 * Shows the console only to a signed-in staff member the API accepts. Every state comes from the server
 * (/v1/auth/me then /v1/console/me); the API enforces the same rules on every console route.
 */
export function ConsoleGate({ children }: { children: ReactNode }) {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: SESSION_KEY, queryFn: loadConsoleSession, retry: false, staleTime: 60_000 });
  const refresh = () => void qc.invalidateQueries({ queryKey: SESSION_KEY });

  if (q.isPending) {
    return (
      <div className="ffa-auth" aria-busy="true" aria-label="Loading">
        <Skeleton />
      </div>
    );
  }
  if (q.isError) {
    return (
      <main className="ffa-auth">
        <div className="ffa-auth__card">
          <h1 className="ffa-auth__title">Can't reach FlexFreight</h1>
          <p className="ffa-auth__lead">The console couldn't load your session. Check your connection and try again.</p>
          <button type="button" className="ffx-btn ffx-btn--primary" onClick={refresh}>
            Try again
          </button>
        </div>
      </main>
    );
  }
  const s = q.data;
  switch (s.state) {
    case "signed_out":
      return <SignIn onDone={refresh} />;
    case "mfa_pending":
      return <VerifyCode onDone={refresh} methods={s.methods} help={s.help} />;
    case "staff_only":
      return <StaffOnly email={s.email} onDone={refresh} />;
    case "mfa_required":
      return <MfaRequired email={s.email} onDone={refresh} />;
    case "ready":
      return <ConsoleMeContext.Provider value={s.me}>{children}</ConsoleMeContext.Provider>;
  }
}
